import {registerGlobalInitFunc} from '../modules/observer.ts';

const securityModalIDs = new Set([
  'security-password-modal',
  'security-password-disabled-modal',
  'security-token-modal',
  'security-session-modal',
]);

const securityModalQueryMap: Record<string, string> = {
  password: 'security-password-modal',
  'password-disabled': 'security-password-disabled-modal',
  token: 'security-token-modal',
  session: 'security-session-modal',
};

let lastSecurityModalOpener: HTMLElement | null = null;

function setButtonLoading(button: HTMLButtonElement, fallbackText: string) {
  button.disabled = true;
  button.classList.add('validist-is-loading');
  button.textContent = button.getAttribute('data-loading-text') || fallbackText;
}

function setFormLoading(form: HTMLFormElement, fallbackText: string) {
  form.setAttribute('aria-busy', 'true');
  for (const button of form.querySelectorAll<HTMLButtonElement>('button[type="submit"]')) {
    setButtonLoading(button, fallbackText);
  }
}

function tokenNames(root: HTMLElement): string[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>('[data-validist-token-name]'),
    (el) => el.getAttribute('data-validist-token-name')?.trim() || '',
  )
    .filter(Boolean);
}

function showTokenCreateError(form: HTMLFormElement, code: string, message: string) {
  const errorBox = form.querySelector<HTMLElement>('[data-validist-token-error]')?.closest<HTMLElement>('.validist-token-inline-error');
  const errorCode = form.querySelector<HTMLElement>('[data-validist-token-error-code]');
  const errorMessage = form.querySelector<HTMLElement>('[data-validist-token-error]');
  const input = form.querySelector<HTMLInputElement>('#sdk_token_name');
  if (!errorBox || !errorCode || !errorMessage || !input) return;

  errorCode.textContent = code;
  errorMessage.textContent = message;
  errorBox.hidden = false;
  input.setAttribute('aria-invalid', 'true');
  input.focus();
}

function clearTokenCreateError(form: HTMLFormElement) {
  const errorBox = form.querySelector<HTMLElement>('.validist-token-inline-error');
  const input = form.querySelector<HTMLInputElement>('#sdk_token_name');
  if (errorBox) errorBox.hidden = true;
  input?.removeAttribute('aria-invalid');
}

function resetTokenModalState(modal: HTMLElement) {
  for (const transient of modal.querySelectorAll<HTMLElement>('[data-validist-token-transient]')) {
    transient.hidden = true;
    transient.setAttribute('aria-hidden', 'true');
  }

  const form = modal.querySelector<HTMLFormElement>('.validist-token-create-form');
  const input = form?.querySelector<HTMLInputElement>('#sdk_token_name');
  if (!form || !input) return;

  form.reset();
  input.value = '';
  clearTokenCreateError(form);
}

function setModalScrollLock(root: HTMLElement, locked: boolean) {
  if (!locked && root.querySelector('.validist-security-modal.is-open, [data-validist-token-delete-modal]:not([hidden])')) return;

  document.documentElement.classList.toggle('validist-modal-lock', locked);
  document.body.classList.toggle('validist-modal-lock', locked);
}

function securityModalFromURL(): string | null {
  const url = new URL(window.location.href);
  const modalFromQuery = url.searchParams.get('modal');
  if (modalFromQuery && securityModalQueryMap[modalFromQuery]) {
    return securityModalQueryMap[modalFromQuery];
  }

  const modalFromHash = url.hash.replace(/^#/, '');
  if (securityModalIDs.has(modalFromHash)) {
    return modalFromHash;
  }

  return null;
}

function clearSecurityModalURL() {
  const url = new URL(window.location.href);
  let shouldReplace = false;

  if (url.searchParams.has('modal')) {
    url.searchParams.delete('modal');
    shouldReplace = true;
  }
  if (securityModalIDs.has(url.hash.replace(/^#/, ''))) {
    url.hash = '';
    shouldReplace = true;
  }
  if (!shouldReplace) return;

  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

function hideNestedTokenDeleteModal(modal: HTMLElement) {
  for (const nestedModal of modal.querySelectorAll<HTMLElement>('[data-validist-token-delete-modal]')) {
    nestedModal.hidden = true;
    nestedModal.setAttribute('aria-hidden', 'true');
  }
}

function openSecurityModal(root: HTMLElement, modalID: string, opener: HTMLElement | null) {
  if (!securityModalIDs.has(modalID)) return;

  const modal = root.querySelector<HTMLElement>(`#${modalID}`);
  if (!modal) return;

  for (const currentModal of root.querySelectorAll<HTMLElement>('.validist-security-modal.is-open')) {
    if (currentModal === modal) continue;
    hideNestedTokenDeleteModal(currentModal);
    currentModal.classList.remove('is-open');
    currentModal.setAttribute('aria-hidden', 'true');
  }

  lastSecurityModalOpener = opener;
  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  setModalScrollLock(root, true);
}

function closeSecurityModal(root: HTMLElement, modal: HTMLElement | null = null) {
  const targetModal = modal || root.querySelector<HTMLElement>('.validist-security-modal.is-open');
  if (!targetModal) return;

  hideNestedTokenDeleteModal(targetModal);
  if (targetModal.id === 'security-token-modal') {
    resetTokenModalState(targetModal);
  }
  targetModal.classList.remove('is-open');
  targetModal.setAttribute('aria-hidden', 'true');
  setModalScrollLock(root, false);

  if (lastSecurityModalOpener && document.contains(lastSecurityModalOpener)) {
    lastSecurityModalOpener.focus();
  }
  lastSecurityModalOpener = null;
}

function initSecurityModals(root: HTMLElement) {
  for (const opener of root.querySelectorAll<HTMLElement>('[data-validist-modal-open]')) {
    opener.addEventListener('click', (e) => {
      const modalID = opener.getAttribute('data-validist-modal-open');
      if (!modalID) return;

      e.preventDefault();
      openSecurityModal(root, modalID, opener);
    });
  }

  for (const closer of root.querySelectorAll<HTMLElement>('[data-validist-modal-close]')) {
    closer.addEventListener('click', (e) => {
      e.preventDefault();
      closeSecurityModal(root, closer.closest<HTMLElement>('.validist-security-modal'));
    });
  }

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeSecurityModal(root);
    }
  });

  if (root.querySelector('.validist-security-modal.is-open')) {
    setModalScrollLock(root, true);
  }

  const modalID = securityModalFromURL();
  if (modalID) {
    openSecurityModal(root, modalID, null);
    clearSecurityModalURL();
  }
}

function initTokenCreateForm(root: HTMLElement) {
  const form = root.querySelector<HTMLFormElement>('.validist-token-create-form');
  const input = form?.querySelector<HTMLInputElement>('#sdk_token_name');
  if (!form || !input) return;

  input.addEventListener('input', () => clearTokenCreateError(form));
  form.addEventListener('submit', (e) => {
    const name = input.value.trim();
    if (!name) {
      e.preventDefault();
      showTokenCreateError(form, 'SDK-TOKEN-400', 'Token adı boş olamaz.');
      return;
    }

    if (tokenNames(root).includes(name)) {
      e.preventDefault();
      showTokenCreateError(form, 'SDK-TOKEN-409', 'Bu isimde token zaten var. Listedeki tokenı silin veya farklı bir isim verin.');
      return;
    }

    clearTokenCreateError(form);
    setFormLoading(form, 'Token üretiliyor');
  });
}

function initTokenDeleteConfirm(root: HTMLElement) {
  const modal = root.querySelector<HTMLElement>('[data-validist-token-delete-modal]');
  const tokenNameEl = modal?.querySelector<HTMLElement>('[data-validist-token-delete-name]');
  const confirmButton = modal?.querySelector<HTMLButtonElement>('[data-validist-token-delete-confirm]');
  if (!modal || !tokenNameEl || !confirmButton) return;

  let pendingForm: HTMLFormElement | null = null;

  const closeModal = () => {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    setModalScrollLock(root, false);
    pendingForm = null;
  };

  const openModal = (form: HTMLFormElement) => {
    pendingForm = form;
    tokenNameEl.textContent = form.getAttribute('data-token-name') || 'SDK tokenı';
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    setModalScrollLock(root, true);
    confirmButton.focus();
  };

  for (const form of root.querySelectorAll<HTMLFormElement>('.validist-token-delete-form')) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      openModal(form);
    });
  }

  for (const cancel of modal.querySelectorAll<HTMLElement>('[data-validist-token-delete-cancel]')) {
    cancel.addEventListener('click', closeModal);
  }

  confirmButton.addEventListener('click', () => {
    if (!pendingForm) return;
    setButtonLoading(confirmButton, 'Siliniyor');
    setFormLoading(pendingForm, 'Siliniyor');
    pendingForm.submit();
  });

  modal.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeModal();
  });
}

function initSessionActionForms(root: HTMLElement) {
  for (const form of root.querySelectorAll<HTMLFormElement>('.validist-session-action-form')) {
    form.addEventListener('submit', () => {
      setFormLoading(form, 'Oturumlar kapatılıyor');
    });
  }
}

export function initValidistCenterSecurity() {
  registerGlobalInitFunc('initValidistCenterSecurity', (el: HTMLElement) => {
    initSecurityModals(el);
    initTokenCreateForm(el);
    initTokenDeleteConfirm(el);
    initSessionActionForms(el);
  });
}
